import { useLocale } from "next-intl";
import {
  blackBath,
  blackDesign,
  blackDimensionalChain,
  blackDwg,
  blackEntryPoint,
  blackFurniture,
  blackKitchen,
  blackMap,
  blackNorthArrow,
  blackRoomNames,
  blackRoomNamesEn,
  blackScale,
  blackSquareMeters,
  dimensionalArchitectStamp,
  dimensionalBath,
  dimensionalDwgIcon,
  dimensionalFactsheet,
  dimensionalFactsheetTableSmall,
  dimensionalFloorPlan,
  dimensionalFurniture,
  dimensionalKitchen,
  dimensionalWoflvIcon,
  grayBath,
  grayDesign,
  grayDimensionalChain,
  grayDwg,
  grayEntryPoint,
  grayFurniture,
  grayKitchen,
  grayMap,
  grayNorthArrow,
  grayRoomNames,
  grayRoomNamesEn,
  grayScale,
  graySquareMeters,
  modernBath,
  modernDesign,
  modernDimensionalChain,
  modernDwg,
  modernEntryPoint,
  modernFurniture,
  modernKitchen,
  modernMap,
  modernNorthArrow,
  modernRoomNames,
  modernRoomNamesEn,
  modernScale,
  modernSquareMeters,
  whiteBath,
  whiteDesign,
  whiteDimensionalChain,
  whiteDwg,
  whiteEntryPoint,
  whiteFurniture,
  whiteKitchen,
  whiteMap,
  whiteNorthArrow,
  whiteRoomNames,
  whiteRoomNamesEn,
  whiteScale,
  whiteSquareMeters,
} from "../../public/images/floor-plan-designs";

interface DesignOption {
  variant?: "salesOptimized" | "dimensional";
  entrance?: boolean;
  furnishings?: boolean;
  kitchenEquipment?: boolean;
  northArrow?: boolean;
  scale?: boolean;
  bathroomEquipment?: boolean;
  squareMetersPerRoom?: boolean;
  roomNames?: boolean;
  factsheet?: boolean;
  livingSpaceCalculation?: boolean;
  architectConfirmation?: boolean;
  livingSpaceArchitectConfirmation?: boolean;
  dimensionalChain?: boolean;
  dwgFile?: boolean;
  locationMap?: boolean;
}

const useDesignsImages = ({
  variant = "salesOptimized",
  entrance,
  furnishings,
  kitchenEquipment,
  northArrow,
  scale,
  bathroomEquipment,
  squareMetersPerRoom,
  roomNames,
  factsheet,
  livingSpaceCalculation,
  architectConfirmation,
  livingSpaceArchitectConfirmation,
  dimensionalChain,
  dwgFile,
  locationMap,
}: DesignOption) => {
  
  const locale = useLocale();

  if (variant === "dimensional") {
    return {
      designs: [
        {
          src: dimensionalFloorPlan,
          alt: "Bemaßter Grundriss",
          name: "black and white",
        },
      ],
      designOptions: [
        {
          design: "black and white",
          option: furnishings,
          src: dimensionalFurniture,
          alt: "Möblierung",
        },
        {
          design: "black and white",
          option: kitchenEquipment,
          src: dimensionalKitchen,
          alt: "Küche",
        },
        {
          design: "black and white",
          option: bathroomEquipment,
          src: dimensionalBath,
          alt: "Bad",
        },
        {
          design: "black and white",
          option: factsheet || livingSpaceArchitectConfirmation,
          src: dimensionalFactsheet,
          alt: "Factsheet",
        },
        {
          design: "black and white",
          option: livingSpaceCalculation,
          src: dimensionalFactsheetTableSmall,
          alt: "Factsheet-Tabelle",
        },
        {
          design: "black and white",
          option: livingSpaceArchitectConfirmation,
          src: dimensionalWoflvIcon,
          alt: "Wohnflächenberechnung",
        },
        {
          design: "black and white",
          option: architectConfirmation,
          src: dimensionalArchitectStamp,
          alt: "Architektenbestätigung",
        },
        {
          design: "black and white",
          option: dwgFile,
          src: dimensionalDwgIcon,
          alt: "DWG-Datei",
        },
      ],
      roomData: [],
    };
  }
  
  const designs = [
    {
      src: blackDesign,
      alt: "Grundriss-Design Schwarz",
      name: "black",
    },
    {
      src: whiteDesign,
      alt: "Grundriss-Design Schwarz-Weiß",
      name: "black and white",
    },
    {
      src: grayDesign,
      alt: "Grundriss-Design Grau",
      name: "gray",
    },
    {
      src: modernDesign,
      alt: "Grundriss-Design Modern",
      name: "modern",
    },
  ];

  const designAssetGroups = [
    {
      design: "black",
      entrance: blackEntryPoint,
      furnishings: blackFurniture,
      kitchen: blackKitchen,
      northArrow: blackNorthArrow,
      scale: blackScale,
      bath: blackBath,
      squareMeters: blackSquareMeters,
      roomNames: locale === "de" ? blackRoomNames : blackRoomNamesEn,
      dimensionalChain: blackDimensionalChain,
      dwg: blackDwg,
      map: blackMap,
    },
    {
      design: "black and white",
      entrance: whiteEntryPoint,
      furnishings: whiteFurniture,
      kitchen: whiteKitchen,
      northArrow: whiteNorthArrow,
      scale: whiteScale,
      bath: whiteBath,
      squareMeters: whiteSquareMeters,
      roomNames: locale === "de" ? whiteRoomNames : whiteRoomNamesEn,
      dimensionalChain: whiteDimensionalChain,
      dwg: whiteDwg,
      map: whiteMap,
    },
    {
      design: "gray",
      entrance: grayEntryPoint,
      furnishings: grayFurniture,
      kitchen: grayKitchen,
      northArrow: grayNorthArrow,
      scale: grayScale,
      bath: grayBath,
      squareMeters: graySquareMeters,
      roomNames: locale === "de" ? grayRoomNames : grayRoomNamesEn,
      dimensionalChain: grayDimensionalChain,
      dwg: grayDwg,
      map: grayMap,
    },
    {
      design: "modern",
      entrance: modernEntryPoint,
      furnishings: modernFurniture,
      kitchen: modernKitchen,
      northArrow: modernNorthArrow,
      scale: modernScale,
      bath: modernBath,
      squareMeters: modernSquareMeters,
      roomNames: locale === "de" ? modernRoomNames : modernRoomNamesEn,
      dimensionalChain: modernDimensionalChain,
      dwg: modernDwg,
      map: modernMap,
    },
  ];

  const designOptions = designAssetGroups.flatMap((group) => [
    {
      design: group.design,
      option: entrance,
      src: group.entrance,
      alt: "Eingang",
    },
    {
      design: group.design,
      option: furnishings,
      src: group.furnishings,
      alt: "Möblierung",
    },
    {
      design: group.design,
      option: kitchenEquipment,
      src: group.kitchen,
      alt: "Küche",
    },
    {
      design: group.design,
      option: northArrow,
      src: group.northArrow,
      alt: "Nordpfeil",
    },
    {
      design: group.design,
      option: scale,
      src: group.scale,
      alt: "Maßstab",
    },
    {
      design: group.design,
      option: bathroomEquipment,
      src: group.bath,
      alt: "Bad",
    },
    {
      design: group.design,
      option: squareMetersPerRoom,
      src: group.squareMeters,
      alt: "Quadratmeter pro Raum",
    },
    {
      design: group.design,
      option: roomNames,
      src: group.roomNames,
      alt: "Zimmernamen",
    },
    {
      design: group.design,
      option: dimensionalChain,
      src: group.dimensionalChain,
      alt: "Dimensional chain",
    },
    {
      design: group.design,
      option: dwgFile,
      src: group.dwg,
      alt: "DWG file",
    },
    {
      design: group.design,
      option: locationMap,
      src: group.map,
      alt: "Location map",
    },
  ]);

  return {
    designs,
    designOptions,
    roomData: [],
  };
};

export default useDesignsImages;
